import { defineComponent, ref, computed } from 'vue'
import {
  Row as VanRow,
  Col as VanCol,
  DropdownMenu as VanDropdownMenu,
  DropdownItem as VanDropdownItem,
} from 'vant'
import type { TukuFace } from '@/api'

export const YearSelector = defineComponent({
  props: {
    list: { type: Array as () => Array<{ id: number | string; name: string }>, required: true },
    value: { type: [Number, String] as unknown as () => number | string | null, default: null },
    onChange: { type: Function as unknown as () => (id: number | string) => void, required: true },
  },
  setup(props) {
    return () => (
      <div class="m-tabs-years">
        <div class="m-tabs-years-bd">
          {/* 样式挂在 `.m-tabs-years-bd span` 上：span 保留，按钮放里面占满一行 */}
          {props.list.map((item) => (
            <span key={item.id} class={{ act: item.id === props.value }}>
              <button type="button" class="btn-reset w-full" onClick={() => props.onChange(item.id)}>
                {item.name}
              </button>
            </span>
          ))}
        </div>
      </div>
    )
  },
})

export const PeriodSelector = defineComponent({
  props: {
    list: { type: Array as () => Array<{ id: number | string; name: string }>, required: true },
    value: { type: [Number, String] as unknown as () => number | string | null, default: null },
    onChange: {
      type: Function as unknown as () => (id: number | string) => void,
      required: true,
    },
  },
  setup(props) {
    const dropdownRef = ref<any>(null)
    const title = computed(() => {
      const item = props.list.find((i) => i.id === props.value)
      return item ? item.name : '请选择'
    })
    function select(item: { id: number | string; name: string }) {
      props.onChange(item.id)
      dropdownRef.value?.toggle()
    }

    /** 下拉面板：全部期数平铺，点选后收起 */
    const renderDropdownBody = () => (
      <div class="dropdown-body">
        <VanRow class="tag-body">
          {props.list.map((item) => (
            <VanCol key={item.id} span={6}>
              <span class={{ act: item.id === props.value }}>
                <button type="button" class="btn-reset w-full" onClick={() => select(item)}>
                  {item.name}
                </button>
              </span>
            </VanCol>
          ))}
        </VanRow>
      </div>
    )
    return () => (
      <div class="m-tabs-period-home">
        <div class="m-tabs-period">
          <div class="m-tabs-period-bd">
            {props.list.map((item) => (
              <span key={item.id} class={{ act: item.id === props.value }}>
                <button type="button" class="btn-reset w-full" onClick={() => props.onChange(item.id)}>
                  {item.name}
                </button>
              </span>
            ))}
          </div>
        </div>
        <VanDropdownMenu>
          <VanDropdownItem ref={dropdownRef} title={title.value}>
            {{ default: renderDropdownBody }}
          </VanDropdownItem>
        </VanDropdownMenu>
      </div>
    )
  },
})

export const FaceSelector = defineComponent({
  props: {
    list: { type: Array as () => TukuFace[], required: true },
    onChange: { type: Function as unknown as () => (face: TukuFace) => void, required: true },
  },
  setup(props) {
    return () => (
      <div class="flex items-center gap-[10Px] px-[12Px] py-[8Px] bg-white">
        {props.list.map((f) => (
          <button
            type="button"
            key={f.pageCode}
            class={[
              'btn-reset inline-flex items-center justify-center min-w-[44Px] h-[28Px] px-[10Px] rounded-full text-[13Px] cursor-pointer select-none',
              f.isCurrent
                ? 'bg-[#07c160] text-white'
                : 'bg-white text-[#333] border border-solid border-[#ddd]',
            ]}
            onClick={() => !f.isCurrent && props.onChange(f)}
          >
            {f.pageName || `${f.pageCode}面`}
          </button>
        ))}
      </div>
    )
  },
})

/** 图库详情 / 投票记录顶部固定条：年份 + 期数，多版面时再加版面切换 */
export function renderGalleryStickyBar(opts: {
  yearList: Array<{ id: number | string; name: string }>
  year: number | null
  onYearChange: (id: number | string) => void
  periodList: Array<{ id: number | string; name: string }>
  period: number | null
  onPeriodChange: (id: number | string) => void
  faces: TukuFace[]
  onFaceChange: (face: TukuFace) => void
}) {
  return (
    <div class="customfixed">
      <div class="sticky-body">
        <YearSelector
          list={opts.yearList}
          value={opts.year ?? undefined}
          onChange={(id) => opts.onYearChange(id)}
        />
        <PeriodSelector
          list={opts.periodList}
          value={opts.period ?? undefined}
          onChange={(id) => opts.onPeriodChange(id)}
        />
        {opts.faces.length > 1 && <FaceSelector list={opts.faces} onChange={opts.onFaceChange} />}
      </div>
    </div>
  )
}
